/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import { LoggerModule } from "@vlinder-web/logger-module-react";

import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { UPDATE_ATTRIBUTE, DELETE_ATTRIBUTE, UPLOAD_FILE } from "../graphql";
import { VApplication } from "../../../application";
import { CREATE_ATTRIBUTE } from "../graphql/createAttribute.mutation";

const is = require("is_js");
const log: any = LoggerModule?.getInstance();

export interface UpdateAttributeProps {
  formData: {
    name?: string;
    year?: string;
    status?: string;
    images?: any[];
    [key: string]: any;
  };
  id: string;
}
export interface CreateAttributeProps {
  formData?: {
    alias?: string;
    description?: string;
    img1?: string;
    img2?: string;
    isLabel?: boolean;
    isVisible?: boolean;
    minDownPayment?: string;
    regMode?: string;
    images?: any[];
    [key: string]: any;
  };
  id: string;
  images?: any[];
  name?: string;
  [key: string]: any;
}
export interface DeleteAttributeProps {
  id: string;
}

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

const normalizeImages = (images: any) => {
  if (!Array.isArray(images)) return images;

  return images.map((image: any) => image?.base64 ?? image).filter(Boolean);
};

export interface IAttributeMethods extends IPluginMethodMap {
  updateAttribute(args: any): Promise<any>;
  createAttribute(args: any): Promise<any>;
  deleteAttribute(args: any): Promise<any>;
}

export class AttributeService implements IAgentPlugin {
  readonly methods: IAttributeMethods;

  constructor() {
    this.methods = {
      updateAttribute: this.updateAttribute.bind(this),
      createAttribute: this.createAttribute.bind(this),
      deleteAttribute: this.deleteAttribute.bind(this),
    };
  }

  async createAttribute(args: CreateAttributeProps) {
    log.blue(`inviteUser server component:`, args);
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    try {
      if (client) {
        log.blue(`inviteUser server component222:`, args);
        const data: any = {
          ...args,
          name: args?.name?.trim(),
        };
        delete data.section;

        if (Object.prototype.hasOwnProperty.call(data, "images")) {
          data.images = await this.uploadFile(
            normalizeImages(data.images),
            data,
          );
        }

        const res = await client.mutate({
          mutation: CREATE_ATTRIBUTE,
          variables: {
            data,
          },
          fetchPolicy: "no-cache",
        });
        if (res?.data) {
          return {
            ...res?.data?.createCostumeAttribute,
          };
        }
      }
    } catch (err: any) {
      let _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);
      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      throw err;
    }
  }

  async updateAttribute(args: UpdateAttributeProps) {
    log.blue(`inviteUser server component:`, args);
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    try {
      if (client) {
        log.blue(`inviteUser server component222:`, args);
        const update: any = {
          ...args?.formData,
          name: args?.formData?.name?.trim(),
        };

        if (Object.prototype.hasOwnProperty.call(update, "images")) {
          update.images = await this.uploadFile(
            normalizeImages(update.images),
            update,
          );
        }

        const res = await client.mutate({
          mutation: UPDATE_ATTRIBUTE,
          variables: {
            filter: {
              _id: args?.id,
            },
            update,
          },
          fetchPolicy: "no-cache",
        });
        if (res?.data) {
          return {
            ...res?.data?.updateCostumeAttribute,
          };
        }
      }
    } catch (err: any) {
      let _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);
      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      throw err;
    }
  }

  async deleteAttribute(args: DeleteAttributeProps) {
    log.blue(`inviteUser server component:`, args);
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    try {
      if (client) {
        const res = await client.mutate({
          mutation: DELETE_ATTRIBUTE,
          variables: {
            filter: {
              _id: args?.id,
            },
          },
          fetchPolicy: "no-cache",
        });
        if (res?.data) {
          return {
            ...res?.data,
          };
        }
      }
    } catch (err: any) {
      let _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);
      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      throw err;
    }
  }

  async uploadFile(arr: any, formData: any) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    const imagesUrlArray: any = [];
    let fileType = "jpeg";

    if (arr && is.not.empty(arr)) {
      for (const obj of arr) {
        if (obj && is.existy(obj)) {
          if (this.isValidHttpUrl(obj)) {
            imagesUrlArray.push(obj);
            continue;
          }

          fileType = this.getFileTypeFromBase64(obj) ?? "jpeg";

          try {
            if (client) {
              const res = await client.mutate({
                mutation: UPLOAD_FILE,
                variables: {
                  name: fileType,
                  data: obj,
                  category1: "costumeattributes",
                  category2: formData?.name ?? "costumeImg",
                  addBandName: true,
                },
                fetchPolicy: "no-cache",
              });

              if (res?.data?.uploadFile) {
                imagesUrlArray.push(res?.data?.uploadFile);
              }
            }
          } catch (err: any) {
            let _display_message =
              err?.graphQLErrors &&
              Array.isArray(err?.graphQLErrors) &&
              err?.graphQLErrors.find((item: any) => item.extensions);
            if (_display_message?.extensions?.display_message) {
              throw Error(_display_message?.extensions?.display_message);
            }

            throw err;
          }
        }
      }
    }

    return imagesUrlArray;
  }

  private isValidHttpUrl(str: string) {
    const urlRegex = /^(ftp|http|https):\/\/[^ "]+$/;
    return urlRegex.test(str);
  }

  private getFileTypeFromBase64(base64String: string) {
    const matches = base64String.match(/^data:image\/(\w+)/);

    if (matches && matches.length === 2) {
      return matches[1];
    }

    return null;
  }
}
